import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Grid, Layout, Menu, Button, Avatar, Popover, Tooltip, Typography, Flex, Alert, Dropdown, App as AntdApp } from 'antd';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MessageOutlined,
  LogoutOutlined,
  SettingOutlined,
  DashboardOutlined,
  TeamOutlined,
  UserOutlined,
  UsergroupAddOutlined,
  CreditCardOutlined,
  DatabaseOutlined,
  BankOutlined,
  KeyOutlined,
  MoonOutlined,
  SunOutlined,
  GlobalOutlined,
  ExclamationCircleOutlined,
  ApiOutlined,
  TagsOutlined
} from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { clearAuthData, getBackToAdmin, isUserOriginAdmin } from '../../../Config/ApiConfig';
import { fbLogout } from '../../../Utils/facebook';
import { useGlobal } from '../GlobalContext/GlobalContext';
import { useErrorHandler } from '../../../Hooks/useErrorHandler';
import { getUser } from './LayoutServices';
import { sendVerificationEmail, updateUser } from '../../Profile/ProfileServices';
import { useTheme } from '../GlobalContext/ThemeContext';
import { ReactComponent as LogoWhite } from '../../../Images/logo-wide-white.svg';
import { ReactComponent as Logo } from '../../../Images/logo-wide.svg';
import { useTranslation } from 'react-i18next';
import { RTL_LANGUAGES } from '../../../Constants/language';
import useSocket from '../../../Hooks/useSocket';
import useSocketSubscription from '../../../Hooks/useSocketSubscription';
import useSocketEvent from '../../../Hooks/useSocketEvent';
import { Resource, Action } from '../../../Constants/socket';
import { COLORS } from '../../../Constants/design';
import { ExportImportProgressProvider } from '../../../Contexts/ExportImportProgressContext';
import ProgressBar from '../ProgressBar/ProgressBar';
const { useBreakpoint } = Grid;
const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { notification } = AntdApp.useApp();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { handleError } = useErrorHandler();
  const { user: { first_name, last_name, picture_url, is_admin, has_received_welcome_credit = true, balance, id, email } = {}, changeUser } = useGlobal();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileSidebarVisible, setMobileSidebarVisible] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [balanceUpdated, setBalanceUpdated] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const screens = useBreakpoint() || {};
  const isOriginAdmin = isUserOriginAdmin();
  const contentBorderColor = isDarkMode ? COLORS.dark.border : '#f0f0f0';

  // Name rendering helpers
  const fullName = [first_name, last_name].filter(Boolean).join(' ').trim();
  const NAME_LIMIT_DESKTOP = 18; // visible in header on >= sm
  const NAME_LIMIT_MOBILE = 12;  // visible inside popover on xs
  const truncate = (text, max) => {
    if (!text || typeof text !== 'string') return '';
    return text.length > max ? text.slice(0, Math.max(0, max - 1)) + '…' : text;
  };

  // Socket connections
  const { socket, connectionState } = useSocket();
  const isConnected = connectionState?.status === 'connected';
  const previousConnectionStatusRef = useRef(connectionState?.status);
  const hasConnectedOnceRef = useRef(false);
  const hadDisconnectedRef = useRef(false);
  const disconnectNotifyTimeoutRef = useRef(null);
  const latestConnectionStatusRef = useRef(connectionState?.status);

  // Listen for coin balance updates
  useSocketEvent(Resource.COIN_BALANCE, Action.UPDATE, (data) => {
    if (data && data.user_id === id) {
      // Update the user's balance in the global context
      changeUser(prevUser => ({
        ...prevUser,
        balance: {
          ...prevUser.balance,
          available_coins: data.available_coins,
          total_coins_bought: data.total_coins_bought,
          total_coins_consumed: data.total_coins_consumed
        }
      }));

      // Trigger animation effect
      setBalanceUpdated(true);
      setTimeout(() => setBalanceUpdated(false), 2000);
    }
  });

  const isRtl = RTL_LANGUAGES.includes(i18n.language.split('-')[0]);

  const handleRetryConnection = useCallback(() => {
    if (connectionState?.status === 'connecting') return;
    try {
      if (socket && typeof socket.connect === 'function') {
        socket.connect();
        notification.warning({
          key: 'socket_connection_status',
          message: t('socket_connection_lost', { defaultValue: 'Connection lost. Trying to reconnect…' }),
          duration: 0,
          btn: (
            <Button size="small" type="primary" loading>
              {t('retry_now', { defaultValue: 'Retry now' })}
            </Button>
          )
        });
      }
    } catch (e) {
      // no-op; error surfaces via existing notification handlers
    }
  }, [socket, connectionState?.status, t]);

  // Notify user on connection lost/restored
  useEffect(() => {
    const previousStatus = previousConnectionStatusRef.current;
    const currentStatus = connectionState?.status;

    if (!previousStatus) {
      previousConnectionStatusRef.current = currentStatus;
      latestConnectionStatusRef.current = currentStatus;
      return;
    }

    if (currentStatus === 'connected') {
      hasConnectedOnceRef.current = true;
    }

    const becameDisconnected = hasConnectedOnceRef.current && previousStatus !== 'disconnected' && currentStatus === 'disconnected';
    const becameConnected = previousStatus === 'disconnected' && currentStatus === 'connected';

    if (becameDisconnected) {
      if (disconnectNotifyTimeoutRef.current) {
        clearTimeout(disconnectNotifyTimeoutRef.current);
      }
      // Debounce showing the disconnect banner to avoid false positives on brief drops
      disconnectNotifyTimeoutRef.current = setTimeout(() => {
        if (latestConnectionStatusRef.current === 'disconnected') {
          notification.warning({
            key: 'socket_connection_status',
            message: t('socket_connection_lost', { defaultValue: 'Connection lost. Trying to reconnect…' }),
            duration: 0,
            btn: (
              <Button size="small" type="text" onClick={handleRetryConnection} loading={connectionState?.status === 'connecting'}>
                {t('retry_now', { defaultValue: 'Retry now' })}
              </Button>
            )
          });
          hadDisconnectedRef.current = true;
        }
      }, 1500);
    }

    if (becameConnected) {
      if (disconnectNotifyTimeoutRef.current) {
        clearTimeout(disconnectNotifyTimeoutRef.current);
        disconnectNotifyTimeoutRef.current = null;
      }
      notification.destroy('socket_connection_status');
      notification.success({
        key: 'socket_connection_status',
        message: t('socket_connection_restored', { defaultValue: 'Connection restored.' }),
        duration: 2
      });
      hadDisconnectedRef.current = false;
    }

    // Safety: always close stale sticky on any connected state
    if (currentStatus === 'connected' && !becameConnected) {
      notification.destroy('socket_connection_status');
    }

    previousConnectionStatusRef.current = currentStatus;
    latestConnectionStatusRef.current = currentStatus;
  }, [connectionState?.status, t]);

  // Cleanup any pending timeout on unmount
  useEffect(() => {
    return () => {
      if (disconnectNotifyTimeoutRef.current) {
        clearTimeout(disconnectNotifyTimeoutRef.current);
      }
    };
  }, []);

  // Sync user data on mount only
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { data } = await getUser();
        changeUser(data);
        if (data?.language && typeof data.language === 'string') {
          const normalized = data.language;
          // Only sync language if it's the initial load to respect user preference
          if (i18n.language !== normalized) {
            i18n.changeLanguage(normalized);
          }
        }
      } catch (error) {
        handleError(error);
      }
    };

    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array ensures this runs only once on mount

  useEffect(() => {
    if (screens.md) {
      setMobileSidebarVisible(false);
    }
  }, [screens.md]);

  const onhandleLogout = async () => {
    setUserMenuOpen(false);
    // Disconnect from socket before logout
    if (socket && socket.disconnect) {
      socket.disconnect();
    }

    await fbLogout();
    navigate('/login');
    clearAuthData();
  };

  const navigateToProfile = () => {
    setUserMenuOpen(false);
    navigate('/profile');
  };

  const toggleCollapse = () => {
    if (screens.md) {
      setCollapsed(!collapsed);
    } else {
      setMobileSidebarVisible(!mobileSidebarVisible);
    }
  };

  const handleVerifyEmail = async () => {
    if (!email) {
      notification.error({ message: t('please_add_email_to_verify') });
      navigate('/profile');
      return;
    }
    setVerificationLoading(true);
    try {
      await sendVerificationEmail(id);
      notification.success({ message: t('verification_email_sent') });
    } catch (error) {
      handleError(error);
    } finally {
      setVerificationLoading(false);
    }
  };

  const backAdminProfile = () => {
    setUserMenuOpen(false);
    const user = getBackToAdmin();
    changeUser(user);
  };

  const menuItems = [
    ...(is_admin ? [{ key: '0', icon: <UsergroupAddOutlined />, label: t('users'), url: '/users' }] : []),
    { key: '1', icon: <DashboardOutlined />, label: t('dashboard'), url: '/' },
    { key: '3', icon: <TeamOutlined />, label: t('agents'), url: '/agents' },
    { key: '5', icon: <MessageOutlined />, label: t('messages'), url: '/messages' },
    { key: '8', icon: <TagsOutlined />, label: t('tickets'), url: '/tickets' },
    { key: '4', icon: <DatabaseOutlined />, label: t('knowledge_base'), url: '/knowledge-base' },
    { key: '2', icon: <CreditCardOutlined />, label: t('billing'), url: '/billing' },
    { key: '6', icon: <BankOutlined />, label: t('transactions'), url: '/transactions' },
    { key: '9', icon: <KeyOutlined />, label: t('credentials'), url: '/credentials' },
    { key: '10', icon: <ApiOutlined />, label: t('channels'), url: '/channels' },
    { key: '11', icon: <DatabaseOutlined />, label: t('data_management', { defaultValue: 'Data Management' }), url: '/data-exports' },
    { key: '7', icon: <SettingOutlined />, label: t('settings'), url: '/settings' },
  ];

  const bottomMenuItems = [
    {
      key: 'language',
      icon: (
        <LanguageSwitcher inSidebar />
      ),
      label: t('language'),
    },
    {
      key: 'theme',
      icon: isDarkMode ? <SunOutlined style={{ color: '#ffd666' }} /> : <MoonOutlined style={{ color: '#597ef7' }} />,
      label: collapsed ? (isDarkMode ? t('light_mode') : t('dark_mode')) : (isDarkMode ? t('light_mode') : t('dark_mode')),
      onClick: toggleDarkMode,
    },
  ];


  const userMenuContent = (
    <div style={{ minWidth: 200 }}>
      {screens.xs && (
        <Flex gap="small" align="center" style={{ padding: 12, borderBottom: '1px solid rgba(0, 0, 0, 0.06)' }}>
          <Avatar size={40} style={{ backgroundColor: '#001529' }} src={picture_url} icon={!picture_url && <UserOutlined />} />
          <Flex vertical>
            <Tooltip title={fullName} placement="bottomLeft">
              <Text strong>{truncate(fullName, NAME_LIMIT_MOBILE)}</Text>
            </Tooltip>
            <Text
              type="secondary"
              style={{
                transition: 'background-color 0.3s ease',
                backgroundColor: balanceUpdated ? (isDarkMode ? 'rgba(0, 230, 118, 0.1)' : 'rgba(0, 230, 118, 0.1)') : 'transparent',
                padding: balanceUpdated ? '0 4px' : '0',
                borderRadius: '2px'
              }}
            >
              {balance?.available_coins} Coins
            </Text>
          </Flex>
        </Flex>
      )}
      <Menu>
        {isOriginAdmin && (
          <Menu.Item key="1" icon={<UserOutlined />} onClick={backAdminProfile}>
            {t('get_back_to_admin')}
          </Menu.Item>
        )}
        <Menu.Item key="2" icon={<UserOutlined />} onClick={navigateToProfile}>
          {t('profile')}
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item key="3" icon={<LogoutOutlined />} onClick={onhandleLogout}>
          {t('log_out')}
        </Menu.Item>
      </Menu>
    </div>
  );

  const activeKey = menuItems.find(item => location.pathname === item.url)?.key;
  const isMessagesRoute = location.pathname.startsWith('/messages');

  const sidebarContent = (
    <div style={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Logo section - fixed */}
      <div dir='ltr' style={{
        height: 32,
        margin: '16px 23px',
        color: isDarkMode ? '#fff' : '#001529',
        fontWeight: 'bold',
        fontSize: 20,
        overflow: 'hidden',
        display: 'block',
        textAlign: 'center',
        flexShrink: 0
      }} align="center" justify="center">
        {isDarkMode && <LogoWhite style={{ width: 'auto', height: '32px' }} />}
        {
          !isDarkMode && <Logo style={{ width: 'auto', height: '32px', }} />
        }
      </div>

      {/* Main menu items - scrollable */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Menu
          mode="inline"
          style={{
            background: isDarkMode ? COLORS.dark.header : '#fff',
            borderInlineEnd: 'none',
            height: '100%'
          }}
          onClick={() => { if (!screens.md) setMobileSidebarVisible(false); }}
          selectedKeys={[activeKey]}
          items={menuItems.map(item => ({
            key: item.key,
            icon: React.cloneElement(item.icon, {
              style: { color: isDarkMode ? '#fff' : '#001529', transition: 'all 0.3s ease' }
            }),
            label: item.url ?
              <Link to={item.url} style={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.88)', transition: 'all 0.3s ease' }}>{item.label}</Link> :
              item.label,
            onClick: item.onClick,
            style: item.style,
            title: typeof item.label === 'string' ? item.label : undefined
          }))}
        />
      </div>

      {/* Bottom menu items - always visible at bottom */}
      <div style={{ flexShrink: 0 }}>
        <Menu
          mode="inline"
          selectable={false}
          selectedKeys={[]}
          style={{
            background: isDarkMode ? COLORS.dark.header : '#fff',
            borderInlineEnd: 'none',
            borderTop: `1px solid ${isDarkMode ? COLORS.dark.border : 'rgba(0, 0, 0, 0.06)'}`
          }}
          onClick={({ key, domEvent }) => {
            if (!screens.md) {
              // Do not close the sidebar when clicking language or theme items on mobile
              if (key === 'language' || key === 'theme') {
                if (domEvent && typeof domEvent.stopPropagation === 'function') {
                  domEvent.stopPropagation();
                }
                return;
              }
              setMobileSidebarVisible(false);
            }
          }}
          items={bottomMenuItems.map(item => ({
            key: item.key,
            icon: React.cloneElement(item.icon, {
              style: { color: isDarkMode ? '#fff' : '#001529', transition: 'all 0.3s ease' }
            }),
            label: item.label,
            onClick: item.onClick,
            title: typeof item.label === 'string' ? item.label : undefined
          }))}
        />
      </div>
    </div>
  );

  return (
    <ExportImportProgressProvider>
      <Layout style={{
        minHeight: '100dvh',
        height: isMessagesRoute ? '100dvh' : undefined,
        overflow: isMessagesRoute ? 'hidden' : undefined,
        background: isDarkMode ? COLORS.dark.background : '#f0f2f5'
      }}>
        {screens.md ? (
          <Sider
            trigger={null}
            collapsible
            collapsed={collapsed}
            style={{
              overflow: 'auto',
              height: '100dvh',
              position: 'fixed',
              left: isRtl ? 'auto' : 0,
              right: isRtl ? 0 : 'auto',
              top: 0,
              bottom: 0,
              zIndex: 2,
              backgroundColor: isDarkMode ? COLORS.dark.header : '#fff',
              borderRight: isRtl ? 'none' : (isDarkMode ? `1px solid ${contentBorderColor}` : '1px solid #f0f0f0'),
              borderLeft: isRtl ? (isDarkMode ? `1px solid ${contentBorderColor}` : '1px solid #f0f0f0') : 'none',
              transition: 'width 0.25s ease'
            }}
          >
            {sidebarContent}
          </Sider>
        ) : (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1000, opacity: mobileSidebarVisible ? 1 : 0, pointerEvents: mobileSidebarVisible ? 'auto' : 'none', transition: 'opacity 0.2s ease' }} onClick={() => setMobileSidebarVisible(false)}>
            <div style={{
              position: 'fixed',
              top: 0,
              left: isRtl ? 'auto' : 0,
              right: isRtl ? 0 : 'auto',
              width: 200,
              height: '100%',
              backgroundColor: isDarkMode ? COLORS.dark.header : '#fff',
              overflowY: 'auto',
              transform: mobileSidebarVisible ? 'translateX(0)' : (isRtl ? 'translateX(100%)' : 'translateX(-100%)'),
              transition: 'transform 0.25s ease'
            }} onClick={(e) => e.stopPropagation()}>
              {sidebarContent}
            </div>
          </div>
        )}
        <Layout className="site-layout" style={{
          marginLeft: isRtl ? 0 : (screens.md ? (collapsed ? 80 : 200) : 0),
          marginRight: isRtl ? (screens.md ? (collapsed ? 80 : 200) : 0) : 0,
          transition: isRtl ? 'margin-right 0.2s' : 'margin-left 0.2s',
          height: isMessagesRoute ? '100%' : 'auto',
          display: isMessagesRoute ? 'flex' : 'block',
          flexDirection: 'column',
          overflow: isMessagesRoute ? 'hidden' : undefined
        }}>
          <Header
            className="site-layout-background"
            style={{
              padding: 0,
              background: isDarkMode ? COLORS.dark.header : '#fff',
              width: '100%',
              zIndex: 1000,
              boxShadow: 'rgba(0, 0, 0, 0.1) 0px 1px 4px 0px',
              borderBottom: isDarkMode
                ? `1px solid ${COLORS.dark.border}`
                : '1px solid rgba(0, 0, 0, 0.06)',
              flexShrink: 0,
              // Sticky on all screen sizes - ensures header is always accessible
              position: 'sticky',
              top: 0,
            }}
          >
            <Flex justify="space-between" align="center" style={{ height: '100%', paddingInlineEnd: 24 }} gap={16}>
              <Button
                type="text"
                icon={
                  !screens.md
                    ? (isRtl ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />)
                    : (isRtl !== collapsed)
                      ? <MenuUnfoldOutlined />
                      : <MenuFoldOutlined />
                }
                onClick={toggleCollapse}
                style={{
                  fontSize: '16px', width: 64, height: 64, color: isDarkMode ? '#fff' : '#001529'
                }}
              />
              <Flex align="center" style={{ flexShrink: 0 }}>
                <Popover
                  content={userMenuContent}
                  trigger="click"
                  placement={isRtl ? 'bottomLeft' : 'bottomRight'}
                  open={userMenuOpen}
                  onOpenChange={setUserMenuOpen}
                >
                  <Flex align="center" style={{ cursor: 'pointer', padding: '8px 0' }}>
                    <Avatar style={{
                      backgroundColor: isDarkMode ? '#001529' : '#f0f0f0',
                      height: 40, width: 40,
                      color: isDarkMode ? '#fff' : '#001529'
                    }} src={picture_url} icon={!picture_url && <UserOutlined />} />
                    {screens.sm && (
                      <Flex vertical style={{ marginInlineStart: 8 }}>
                        <Tooltip title={fullName} placement={isRtl ? 'bottomLeft' : 'bottomRight'}>
                          <Text style={{ color: isDarkMode ? '#fff' : '#001529' }}>{truncate(fullName, NAME_LIMIT_DESKTOP)}</Text>
                        </Tooltip>
                        <Text
                          style={{
                            color: isDarkMode ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.88)',
                            fontWeight: 'bold',
                            transition: 'color 0.3s ease',
                            backgroundColor: balanceUpdated ? (isDarkMode ? 'rgba(0, 230, 118, 0.1)' : 'rgba(0, 230, 118, 0.1)') : 'transparent',
                          }}
                        >
                          {balance?.available_coins} {t('coins')}
                        </Text>
                      </Flex>
                    )}
                  </Flex>
                </Popover>
              </Flex>
            </Flex>
          </Header>
          <ProgressBar />
          <Content
            theme={isDarkMode ? 'dark' : 'light'}
            style={{
              padding: 16,
              flex: 1,
              height: isMessagesRoute ? '100%' : 'auto',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: isMessagesRoute ? 'hidden' : undefined,
              overscrollBehavior: isMessagesRoute ? 'none' : undefined
            }}
          >
            {/* Connection status banner replaced by notifications */}
            {!has_received_welcome_credit && (
              <Alert
                message={t('verify_email_and_get_free_coins')}
                type="info"
                showIcon
                style={{ width: '100%', marginBottom: 8 }}
                action={<Button size="small" type="primary" loading={verificationLoading} onClick={handleVerifyEmail}>{t('verify_now')}</Button>}
                closable
              />
            )}
            {children}
          </Content>
        </Layout>
      </Layout>
    </ExportImportProgressProvider>
  );
};

export default AdminLayout;
